import { Stack, Typography } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Button } from 'settle-ui/components/Button/Button'
import { Modal } from 'settle-ui/components/Modal/Modal'
import { useSnackbars } from 'settle-ui/hooks/useSnackbars/useSnackbars'
import { Link } from 'settle-ui/components/Link/Link'
import { PromoCodeIcon } from 'routes/components/PromoCodeIcon/PromoCodeIcon'
import { logEvent } from 'lib/tracking/segment'
import {
  type PromoCodeDetails,
  useAppliedPromoCodeContext,
} from 'routes/hooks/useAppliedPromoCodeContext/useAppliedPromoCodeContext'
import * as events from './ViewPromoCodeDialog.events'

interface ViewPromoCodeDialogProps {
  open: boolean
  onClose: () => void
  onProceed?: (promoCode: PromoCodeDetails) => Promise<void> | void
}

export const ViewPromoCodeDialog: Settle.FC<ViewPromoCodeDialogProps> = ({ open, onClose, onProceed }) => {
  const { t } = useTranslation()
  const { showError } = useSnackbars()
  const [loading, setLoading] = useState(false)
  const { promoCode } = useAppliedPromoCodeContext()
  const { description } = promoCode ?? {}
  const handleCancel = () => {
    void logEvent(events.CANCEL)
    onClose()
  }

  return (
    <Modal open={open} onClose={onClose} data-testid="view-promo-code-dialog">
      <Modal.Header onClose={onClose} />
      <Modal.Content>
        <Stack gap={2}>
          <PromoCodeIcon sx={{ alignSelf: 'center' }} size="large" />
          <Typography alignSelf="center" variant="h6" maxWidth="80%" textAlign="center">
            {promoCode?.displayName}
          </Typography>
          {description && (
            <Typography color="textSecondary" variant="body1">
              {description}
            </Typography>
          )}
          <Typography color="textSecondary" variant="body2">
            <Trans
              i18nKey={
                onProceed ? 'viewPromoCodeDialog::termsConditions::apply' : 'viewPromoCodeDialog::termsConditions::view'
              }
              components={{
                termsLink: <Link variant="inherit" disabled={!promoCode} href={promoCode?.termsLink} target="_blank" />,
              }}
            />
          </Typography>
        </Stack>
      </Modal.Content>
      <Modal.Footer
        rightButtons={
          onProceed && (
            <>
              <Button onClick={handleCancel}>{t('cancel')}</Button>
              <Button
                loading={loading}
                variant="primary"
                data-testid="submit-btn"
                onClick={async () => {
                  if (promoCode) {
                    void logEvent(events.APPLY, { code: promoCode.code })
                    setLoading(true)
                    await onProceed(promoCode)
                    setLoading(false)
                  } else {
                    showError(t('somethingWentWrong'))
                  }
                }}
              >
                {t('viewPromoCodeDialog::apply')}
              </Button>
            </>
          )
        }
      />
    </Modal>
  )
}
